import Highcharts from 'highcharts/highstock';
import { StatRow } from 'oas';
import { SelectedParameters } from 'reporting/reducers';
import { ExtendedAthlete } from 'reporting/types';
import {
    CUSTOM_UNIT_TYPE,
    MAX_REPORTING_PARAMETERS,
    PER_MIN_REPORTING_PARAMETERS,
    STACKED_GRAPH_AVG_PARAMETERS,
} from '../../../constants';
import { TfParameter } from 'api';

type MaxYValues = {
    [key: `y${number}Max`]: number;
};

export function getMaxYBySelectedParameters(
    stats: ExtendedAthlete[] | StatRow[],
    selectedParameters: SelectedParameters | TfParameter[],
    // default to 115% of the max value for chart spacing
    multiplier = 1.15,
): MaxYValues {
    const maxes: MaxYValues = {};
    // enable conditional use of old params format, or new one
    const selected = 'paramList' in selectedParameters ? selectedParameters.paramList : selectedParameters;

    const paramMaxes: number[] = selected.map(() => 0);

    stats.forEach((stat) => {
        const arr = 'athleteName' in stat ? stat.athleteStats : stat;

        selected.forEach((param, i) => {
            if (arr[param.slug] > paramMaxes[i]) {
                paramMaxes[i] = arr[param.slug];
            }
        });
    });

    selected.forEach((_, i) => {
        const key = 'y' + (i + 1) + 'Max';
        maxes[key as keyof MaxYValues] = paramMaxes[i] * multiplier;
    });

    return maxes;
}

export const getDefaultScrollbar = () => {
    return {
        enabled: true,
        showFull: false,
        barBorderRadius: 6,
        barBorderWidth: 0,
        barBorderColor: 'var(--grey-5)',
        buttonBackgroundColor: 'var(--r-catapult-white)',
        buttonBorderWidth: 0,
        buttonArrowColor: 'var(--r-catapult-white)',
        minWidth: 40,
        height: 12,
        margin: 16,
        rifleColor: 'var(--grey-5)',
        trackBackgroundColor: 'var(--r-catapult-white)',
        trackBorderRadius: 0,
        trackBorderWidth: 0,
    };
};

export const getDSLabelStyle = () => {
    return {
        fontSize: '12px',
        lineHeight: '20px',
        fontWeight: '500',
        fontStyle: 'normal',
        fontFamily: 'Roboto, sans-serif',
        letterSpacing: '0.5px',
        color: 'black',
    };
};

export const getXAxisIntervalConfig = (
    useNumericIntervals: boolean,
    intervalSecs: number,
    title = 'Time',
    tickGap = false,
    format = '%H:%M:%S',
) => {
    return {
        type: useNumericIntervals ? 'linear' : 'datetime',
        tickInterval: intervalSecs * (useNumericIntervals ? 1 : 1000),
        title: {
            text: title,
            style: getAxisLabelStyle(),
        },
        labels: {
            formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                if (useNumericIntervals) {
                    return formatNumericInterval(this.value as number, intervalSecs);
                }
                return Highcharts.dateFormat(format, this.value as number);
            },
            style: {
                fontSize: '12',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                color: 'black',
            },
        },
        lineColor: 'black',
        lineWidth: 1,
        startOnTick: tickGap,
        endOnTick: tickGap,
        tickWidth: 1,
        tickmarkPlacement: 'on',
        gridLineWidth: 1,
        gridLineDashStyle: 'Dash',
    };
};

export const formatNumericInterval = (value: number, intervalSecs: number): string => {
    const startValue = value;
    const endValue = value + intervalSecs;
    return `${startValue}-${endValue}`;
};

function getValueToFormat(sum: number, max: number, avg: number, param: TfParameter) {
    if (Object.values(MAX_REPORTING_PARAMETERS).includes(param.slug)) {
        return max;
    } else if (
        PER_MIN_REPORTING_PARAMETERS[param.slug] !== undefined ||
        STACKED_GRAPH_AVG_PARAMETERS.includes(param.slug)
    ) {
        return avg;
    } else {
        return sum;
    }
}

// eslint-disable-next-line
export function getStackedChartMaxY(series: any) {
    const maxLength = Math.max(...series.map((obj: { data: string | number[] }) => obj.data.length));

    if (maxLength === -Infinity) {
        return 0;
    }

    const sums = Array(maxLength).fill(0);

    series.forEach((obj: { data: number[] }) => {
        obj.data.forEach((value, index) => {
            sums[index] += value;
        });
    });

    return Math.max(...sums);
}

export function formatStackLabel(
    label: Highcharts.StackItemObject,
    param: TfParameter,
    formatMetric: (key: string | undefined, val: number, isAverage?: boolean) => string | number,
): string | number {
    let sum = 0;
    let max = 0;
    let avg = 0;

    const x = label.x;
    const series = label.axis.chart.series.filter((s) => s.visible === true);

    if (param) {
        series
            // eslint-disable-next-line
            .forEach((s: any) => {
                if (s.options.data[x] > max) {
                    max = s.options.data[x];
                }

                sum += s.options.data[x];
            });

        avg = sum / series.length;

        const customType = CUSTOM_UNIT_TYPE.find((type) => type.list.includes(param.slug));
        const val = getValueToFormat(sum, max, avg, param);

        return formatMetric(
            customType ? customType.unitInfo?.unitType : param.unit_type,
            val ?? 0,
            customType ? customType.unitInfo?.isAverage : false,
        );
    }

    return '0';
}

export const getColourFromVar = (val: string) => {
    return window.getComputedStyle(document.documentElement).getPropertyValue(val);
};

const reportingColours = [
    getColourFromVar('--data-vis-1'),
    getColourFromVar('--data-vis-2'),
    getColourFromVar('--data-vis-3'),
];

export const extendedReportingColours = [
    getColourFromVar('--data-vis-1'),
    getColourFromVar('--data-vis-2'),
    getColourFromVar('--data-vis-3'),
    getColourFromVar('--data-vis-4'),
    getColourFromVar('--data-vis-3-variant'),
    getColourFromVar('--data-vis-1-variant'),
    getColourFromVar('--data-vis-2-variant'),
    getColourFromVar('--data-vis-4-variant'),
];

export const defaultEffortsColours = [
    extendedReportingColours[0],
    extendedReportingColours[1],
    extendedReportingColours[2],
    extendedReportingColours[3],
    extendedReportingColours[5],
    extendedReportingColours[6],
    extendedReportingColours[4],
    extendedReportingColours[7],
];

export const createColourCycler = (useExtendedColours?: boolean) => {
    const colours = useExtendedColours ? extendedReportingColours : reportingColours;
    let index = 0;

    return () => {
        const nextColour = colours[index];
        index = (index + 1) % colours.length;
        return nextColour;
    };
};

export const isColorDark = (colorHex: string): boolean => {
    const hex = colorHex.startsWith('#') ? colorHex.slice(1) : colorHex;

    if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        throw new Error('Invalid color format');
    }

    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // https://www.w3.org/WAI/GL/wiki/Relative_luminance essentially weighted ratios for colours to determine perceived brightness
    const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return brightness < 128; // color is considered dark at values below 128
};

export const getAxisLabelStyle = () => {
    return {
        fontFamily: 'Roboto',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.1px',
        color: '#101010',
        textTransform: 'capitalize',
    };
};

export const pushPlotBand = (from: number, to: number, text: string, bands: Highcharts.XAxisOptions['plotBands']) => {
    if (bands) {
        bands.push({
            color:
                bands.length > 0 && bands[bands.length - 1].color === 'var(--r-grey-7)'
                    ? ' var(--r-catapult-white)'
                    : 'var(--r-grey-7)',
            from: from,
            to: to,
            label: {
                text: text,
                align: 'left',
                x: 5,
                style: {
                    fontFamily: 'Roboto',
                    fontSize: '12',
                },
            },
        });
    }
};

export const pushPlotLine = (x: number, color: string, lines: Highcharts.XAxisOptions['plotLines']) => {
    if (lines) {
        lines.push({
            color: color,
            value: x,
            dashStyle: 'ShortDash',
            width: 1,
            zIndex: 10,
        });
    }
};

export const getVerticalDataLabels = (
    p: TfParameter,
    formatMetric: (key: string | undefined, val: number, isAverage?: boolean | undefined) => string | number,
) => {
    return {
        enabled: true,
        rotation: 270,
        x: 0,
        y: -20,
        formatter: function () {
            const customType = CUSTOM_UNIT_TYPE.find((type) => type.list.includes(p.slug));
            return formatMetric(
                customType ? customType.unitInfo?.unitType : p.unit_type,
                this.y ?? 0,
                customType ? customType.unitInfo?.isAverage : false,
            );
        },
    };
};

export const matchDayModeLine: Highcharts.SeriesLineOptions = {
    name: '100%',
    data: [[0, 100], { x: 0, y: 100 }],
    type: 'line',
    color: 'var(--r-grey-3)',
    dashStyle: 'ShortDash',
    lineWidth: 2.5,
    enableMouseTracking: false,
    marker: { enabled: false },
};

export const sortStats = (sortBy: string, reverseSort: boolean, stats: StatRow[]) => {
    let sorted = [];

    if (sortBy === 'name') {
        sorted = stats.sort(function (a, b) {
            return !reverseSort
                ? a['athlete_name'].localeCompare(b['athlete_name'])
                : b['athlete_name'].localeCompare(a['athlete_name']);
        });
    } else {
        sorted = stats.sort(function (a, b) {
            return !reverseSort ? a[sortBy] - b[sortBy] : b[sortBy] - a[sortBy];
        });
    }

    return sorted;
};

export const formatSecondsAsHHMMSS = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return [
        hours.toString().padStart(2, '0'),
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
    ].join(':');
};
